import { message } from 'antd';
import {
  useSetFollowedStoreListState,
  useFollowedStoreListState,
} from 'hooks/business/conversation';
import { uniqBy } from 'lodash-es';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslate } from '@tolgee/react';
import { changeFollowStatus, clearAllFollowList, StoreInfo } from 'services/api/store';

import { MAX_SELECTED_LENGTH } from './useStoreSelector';

const useFollowList = (platform?: General.PlatformUpperCaseTunnel) => {
  const { t } = useTranslate();
  const setFollowList = useSetFollowedStoreListState();
  const storeList = useFollowedStoreListState();

  const onChangeFollowStatus = useCallback(
    (store: StoreInfo, followed: boolean) => {
      if (storeList.length >= MAX_SELECTED_LENGTH && followed) {
        message.warn(t('storeSelector.最多可关注{x}家店铺', { num: 100 }));
        return Promise.reject();
      }
      return changeFollowStatus(store.tunnelAccountId, followed)
        .then(() => {
          if (followed) {
            setFollowList((pre) => uniqBy([...pre, store], 'storeId'));
          } else {
            setFollowList((pre) => pre.filter((x) => x.storeId !== store.storeId));
          }
        })
        .catch(() => message.error(t('storeSelector.设置店铺关注失败')));
    },
    [setFollowList, storeList.length, t],
  );
  const onClearAllFollow = useCallback(() => {
    clearAllFollowList()
      .then(() => {
        setFollowList([]);
      })
      .catch(() => message.error(t('storeSelector.设置店铺关注失败')));
  }, [setFollowList, t]);
  const isFollowed = useCallback(
    (storeId: string) => {
      return !!storeList.find((x) => x.storeId === storeId);
    },
    [storeList],
  );
  const currentPlatformFollowList = useMemo(() => {
    if (!platform) {
      return storeList;
    }
    return storeList.filter((x) => x.externalType === platform);
  }, [storeList, platform]);

  useEffect(() => {
    setFollowList(storeList);
  }, [setFollowList, storeList]);

  return {
    allFollowList: storeList,
    onChangeFollowStatus,
    onClearAllFollow,
    isFollowed,
    currentPlatformFollowList,
  };
};

export default useFollowList;
